import React, { useState } from "react";

import { Button, Modal } from "react-bootstrap";

import { FieldValues } from "react-hook-form";
import { ReactSVG } from "react-svg";

import { useVerifyIdentityMutation } from "@api/graphql";
import { CrossIcon } from "@assets/icons";
import { ToastMessage } from "@components/ToastMessage";
import Checkbox from "@components/component-library/Atoms/Checkbox/Checkbox";
import RadioButtonGroup from "@components/component-library/Atoms/RadioButtonGroup/RadioButtonGroup";
import Texfield from "@components/component-library/Atoms/TextField/Texfield";
import Form from "@components/component-library/Molecules/Form/Form";

import { prepareVerifyIdentityInput } from "./IdCheckModal.helpers";
import IdCheckModalDocumentSection from "./IdCheckModalDocumentSection";

const IdCheckModal = ({
  show,
  setShow,
  name,
  participant_id,
  refetch,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
  participant_id: number;
  refetch: () => void;
}) => {
  const [verifyIdentity] = useVerifyIdentityMutation({});

  const [toastMessageData, setToastMessageData] = useState<{
    message: string;
    variant?: "error" | "success" | "info";
  }>();

  const [showToast, setShowToast] = useState(false);

  return (
    <div>
      <ToastMessage
        message={toastMessageData?.message || ""}
        show={showToast}
        setShow={setShowToast}
        type={toastMessageData?.variant}
      />
      <Modal show={show} onHide={() => setShow(false)} size="lg" centered contentClassName="px-2 py-4">
        <Modal.Header>
          <div className="d-flex justify-content-between w-100 monoBlack">
            <div>
              <h5>Complete identification check {name}</h5>
              <div className="b4 medium monoBlack">
                If your name appears differently on your ID documents please call our Member Services team on{" "}
                <span className="text-nowrap">1800 066 844</span>
              </div>
            </div>
            <Button variant="plain">
              <ReactSVG src={CrossIcon} onClick={() => setShow(false)} />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={async (data: FieldValues) => {
              const res = await verifyIdentity({
                variables: {
                  input: prepareVerifyIdentityInput(data, participant_id),
                },
              });
              if (res.data?.verifyIdentity?.verified) {
                setToastMessageData({
                  message: "ID verification successfully completed.",
                  variant: "success",
                });
                refetch();
                setShowToast(true);
                setShow(false);
              } else {
                setToastMessageData({
                  message: "ID verification failed. Please contact Member services.",
                  variant: "error",
                });
                setShowToast(true);
                setShow(false);
              }
            }}
          >
            <RadioButtonGroup
              name="method"
              legend="How do you want to confirm your identity"
              radioOptions={[
                { label: "Australian Drivers Licence", defaultChecked: true },
                { label: "Australian Passport" },
                { label: "Medicare Card" },
                { label: "Centrelink Card" },
                { label: "New Zealand Drivers Licence" },
                { label: "New Zealand Passport" },
              ]}
              defaultValue="Australian Drivers Licence"
              screenSpan="full-screen"
            />

            <IdCheckModalDocumentSection />
            <Texfield
              label="* Date of birth"
              name="date_of_birth"
              placeholder="As displayed on your ID"
              rules={{ required: "Date of birth is required" }}
              screenSpan="full-screen"
              type="date"
            />
            <Modal.Footer className="px-0 justify-content-start border-top-0">
              <h5 className="m-0">Our commitment</h5>
              <div className="monoBlack b4 medium m-0 mb-2">
                We will not store this information and will only use it for the purposes of verifying your identity.
                We’ll treat your personal information confidentially, and in line with our Privacy Policy.
              </div>
              <div className="m-0">
                <Checkbox
                  name="consent"
                  label="I consent to my information being checked securely by an authorised third party for the purpose of verifying my identity for any future resale royalty payments."
                  rules={{ required: "Consent is required" }}
                />
              </div>
              <Button type="submit" className="my-4 mx-auto">
                Submit
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default IdCheckModal;
